import {Component} from './component';
import mermaid from 'mermaid';

export class MarkdownEditor extends Component {

    setup() {
        this.elem = this.$el;

        this.pageId = this.$opts.pageId;
        this.textDirection = this.$opts.textDirection;
        this.imageUploadErrorText = this.$opts.imageUploadErrorText;
        this.serverUploadLimitText = this.$opts.serverUploadLimitText;

        this.display = this.$refs.display;
        this.input = this.$refs.input;
        this.divider = this.$refs.divider;
        this.displayWrap = this.$refs.displayWrap;

        const {settingContainer} = this.$refs;
        const settingInputs = settingContainer.querySelectorAll('input[type="checkbox"]');

        this.editor = null;
        window.importVersioned('markdown').then(markdown => {
            return markdown.init({
                pageId: this.pageId,
                container: this.elem,
                displayEl: this.display,
                inputEl: this.input,
                drawioUrl: this.getDrawioUrl(),
                settingInputs: Array.from(settingInputs),
                text: {
                    serverUploadLimit: this.serverUploadLimitText,
                    imageUploadError: this.imageUploadErrorText,
                },
            });
        }).then(editor => {
            this.editor = editor;
            this.setupListeners();
            this.emitEditorEvents();
            this.scrollToTextIfNeeded();
            this.editor.actions.updateAndRender();
            this.watchDisplayForMermaid();
        });
    }

    emitEditorEvents() {
        window.$events.emitPublic(this.elem, 'editor-markdown::setup', {
            markdownIt: this.editor.markdown.getRenderer(),
            displayEl: this.display,
            cmEditorView: this.editor.cm,
        });
    }

    setupListeners() {
        // Button actions
        this.elem.addEventListener('click', event => {
            const button = event.target.closest('button[data-action]');
            if (button === null) return;

            const action = button.getAttribute('data-action');
            if (action === 'insertImage') this.editor.actions.showImageInsert();
            if (action === 'insertLink') this.editor.actions.showLinkSelector();
            if (action === 'insertDrawing' && (event.ctrlKey || event.metaKey)) {
                this.editor.actions.showImageManager();
                return;
            }
            if (action === 'insertDrawing') this.editor.actions.startDrawing();
            if (action === 'fullscreen') this.editor.actions.fullScreen();
        });

        // Mobile section toggling
        this.elem.addEventListener('click', event => {
            const toolbarLabel = event.target.closest('.editor-toolbar-label');
            if (!toolbarLabel) return;

            const currentActiveSections = this.elem.querySelectorAll('.markdown-editor-wrap');
            for (const activeElem of currentActiveSections) {
                activeElem.classList.remove('active');
            }

            toolbarLabel.closest('.markdown-editor-wrap').classList.add('active');
        });

        this.handleDividerDrag();
    }

    handleDividerDrag() {
        this.divider.addEventListener('pointerdown', () => {
            const wrapRect = this.elem.getBoundingClientRect();
            const moveListener = event => {
                const xRel = event.pageX - wrapRect.left;
                const xPct = Math.min(Math.max(20, Math.floor((xRel / wrapRect.width) * 100)), 80);
                this.displayWrap.style.flexBasis = `${100 - xPct}%`;
                this.editor.settings.set('editorWidth', xPct);
            };
            const upListener = () => {
                window.removeEventListener('pointermove', moveListener);
                window.removeEventListener('pointerup', upListener);
                this.display.style.pointerEvents = null;
                document.body.style.userSelect = null;
            };

            this.display.style.pointerEvents = 'none';
            document.body.style.userSelect = 'none';
            window.addEventListener('pointermove', moveListener);
            window.addEventListener('pointerup', upListener);
        });
        const widthSetting = this.editor.settings.get('editorWidth');
        if (widthSetting) {
            this.displayWrap.style.flexBasis = `${100 - widthSetting}%`;
        }
    }

    scrollToTextIfNeeded() {
        const queryParams = (new URL(window.location)).searchParams;
        const scrollText = queryParams.get('content-text');
        if (scrollText) {
            this.editor.actions.scrollToText(scrollText);
        }
    }

    getDrawioUrl() {
        const drawioAttrEl = document.querySelector('[drawio-url]');
        if (!drawioAttrEl) {
            return '';
        }
        return drawioAttrEl.getAttribute('drawio-url') || '';
    }

    async getContent() {
        return this.editor.actions.getContent();
    }

    watchDisplayForMermaid() {
        mermaid.initialize({
                startOnLoad: false,
                securityLevel: 'loose',
                theme: 'base',
                themeVariables: {
                    primaryColor: '#F7F0EB',
                    primaryTextColor: '#3D2B1F',
                    primaryBorderColor: '#8B7668',
                    secondaryColor: '#EBF0F5',
                    secondaryTextColor: '#2C3E50',
                    secondaryBorderColor: '#5A7A94',
                    tertiaryColor: '#F5F2EE',
                    tertiaryTextColor: '#4A4540',
                    tertiaryBorderColor: '#9E958B',
                    lineColor: '#5C5552',
                    textColor: '#3D2B1F',
                    mainBkg: '#F7F0EB',
                    nodeBorder: '#8B7668',
                    fontFamily: 'sans-serif',
                    fontSize: '14px',
                },
                flowchart: {
                    htmlLabels: true,
                    curve: 'linear',
                    padding: 16,
                    nodeSpacing: 50,
                    rankSpacing: 60,
                    useMaxWidth: true,
                },
            });

        this.editor.display.onPatchCallback = () => {
            if (this._mermaidRendering) return;
            this._mermaidRendering = true;
            this.renderMermaidInIframe().finally(() => {
                this._mermaidRendering = false;
            });
        };

        // 초기 렌더링
        this.renderMermaidInIframe();
    }

    async renderMermaidInIframe() {
        const doc = this.display.contentDocument;
        if (!doc || !doc.body) return;

        const blocks = doc.querySelectorAll('pre code.language-mermaid, pre code[class*="mermaid"]');
        if (blocks.length === 0) return;

        if (!this._mermaidSvgCache) this._mermaidSvgCache = new Map();

        for (const block of blocks) {
            const pre = block.parentElement;
            const code = block.innerText.trim();
            if (!code) continue;

            // 성공 캐시: 바로 SVG 주입
            if (this._mermaidSvgCache.has(code)) {
                const div = doc.createElement('div');
                div.innerHTML = this._mermaidSvgCache.get(code);
                pre.replaceWith(div);
                continue;
            }

            const id = 'mermaid-' + Math.random().toString(36).slice(2);

            try {
                const { svg } = await mermaid.render(id, code);
                // 캐시 최대 40개 제한
                if (this._mermaidSvgCache.size >= 40) {
                    const firstKey = this._mermaidSvgCache.keys().next().value;
                    this._mermaidSvgCache.delete(firstKey);
                }
                this._mermaidSvgCache.set(code, svg);
                const div = doc.createElement('div');
                div.innerHTML = svg;
                pre.replaceWith(div);
            } catch(e) {
                const errEl = document.getElementById(id);
                if (errEl) errEl.remove();

            }
        }
    }

}
